import React, { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const ISSUE_LABEL = {
  ziel_fehlt: "Ziel fehlt",
  abschnitt_fehlt: "Abschnitt fehlt",
};
const ISSUE_CLS = {
  ziel_fehlt: "bg-amber-50 border-amber-200 text-amber-800",
  abschnitt_fehlt: "bg-sky-50 border-sky-200 text-sky-800",
};

export default function DataAuditTab() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/data-audit", { timeout: 60000 });
      setAudit(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="data-audit-tab">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
            <ShieldAlert size={11} strokeWidth={1.5} /> Automatische Datenprüfung
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
            Auffälligkeiten bei AVGS-Kunden
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Prüft automatisch alle Kunden auf: (1) Abschnitte/Onboarding/Offboarding mit UE-Ziel
            0, bei denen trotzdem schon UE gebucht wurden (sieht wie "2 / 0 UE" aus), (2)
            unterschriebene Termine/Einträge ohne Abschnitt-Zuordnung. Die UE selbst wurden in
            beiden Fällen korrekt gezählt — nur die Zuordnung/Konfiguration ist unvollständig.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="data-audit-refresh-btn"
          className="px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={12} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> {loading ? "Prüft…" : "Neu prüfen"}
        </button>
      </div>

      {!audit ? (
        <div className="text-sm text-slate-400 italic py-10 text-center">Lädt…</div>
      ) : audit.flagged_count === 0 ? (
        <div className="border border-emerald-200 bg-emerald-50 p-6 text-center text-emerald-800 text-sm" data-testid="data-audit-all-clean">
          Keine Auffälligkeiten gefunden — alle {audit.checked_count} geprüften Kunden sind konsistent.
        </div>
      ) : (
        <div className="border border-slate-200 bg-white" data-testid="data-audit-results-table">
          <div className="p-4 border-b border-slate-200 text-xs text-slate-500">
            {audit.flagged_count} von {audit.checked_count} Kunden mit Auffälligkeiten
          </div>
          <ul className="divide-y divide-slate-100">
            {audit.clients.map((c) => (
              <li key={c.client_id} className="p-4" data-testid={`data-audit-row-${c.client_id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <span className="text-sm font-medium text-slate-900">{c.client_name}</span>
                  <Link to={`/avgs/${c.client_id}`} target="_blank" data-testid={`data-audit-open-${c.client_id}`}
                    className="text-[11px] text-[#00807E] hover:underline flex items-center gap-1">
                    Zur Kunden-Akte <ExternalLink size={10} strokeWidth={1.5} />
                  </Link>
                </div>
                <div className="flex flex-col gap-1.5">
                  {c.issues.map((iss, idx) => (
                    <div key={idx} className={`text-xs px-2.5 py-1.5 border ${ISSUE_CLS[iss.type] || "bg-slate-50 border-slate-200 text-slate-700"}`}>
                      <span className="font-bold mr-1.5">{ISSUE_LABEL[iss.type] || iss.type}:</span>
                      {iss.detail}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
